import _ from 'lodash'
import React, { useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { CSSTransition } from 'react-transition-group'
import { Input, Search, SearchResultProps } from 'semantic-ui-react'
import { SearchProps, SearchResultData } from 'semantic-ui-react/dist/commonjs/modules/Search/Search'
import { cancelAddressSearchRequest } from '../../apis/AddressServiceAPI'
import {
  AddressSearchResultsData,
  areAddressesLoading,
  getAddresses,
  getCandidateAddresses,
} from '../../common/candidateAddresses.slice'
import { useAppDispatch, useAppSelector } from '../../common/store'
import { replaceSpaceWithX } from '../../common/util'

import './PlanningSearchBar.css'
import {
  getPlanningSearchValue,
  updatePlanningSearchValue,
} from './slice'

/** ADDRESS SEARCH ***/
let performAddressSearch = _.debounce(
  (value, dispatch) => {
    dispatch(getCandidateAddresses(value))
    dispatch(updatePlanningSearchValue(value))
  }, 300, {
    leading: true,
    trailing: true,
  },
)

type props = {
  urlSubPath: string
}

function PlanningSearchBar({ urlSubPath }: props) {

  const dispatch = useAppDispatch()
  const candidateAddresses = useAppSelector(getAddresses)
  const isLoading = useAppSelector(areAddressesLoading)
  const planningSearchValue = useAppSelector(getPlanningSearchValue)
  const history = useHistory()
  const planningDetailsSearchRef = useRef<HTMLDivElement>(null)

  //Build the results prop to be passed to the Search component
  const getResults = (parcels: AddressSearchResultsData[]) => {
    if (parcels) {
      return parcels.map(
        parcel => ({ key: parcel.GNAF_PID, title: parcel.SearchTerm, laiskey: parcel.LAIS_KEY }),
      )
    }
  }

  /**
   * semantic-ui-react Search callback
   *
   * Called when a result is selected
   *
   * @param e
   * @param result
   */
  const handleResultSelect = (e: React.MouseEvent<HTMLDivElement>, { result }: SearchResultData) => {
    let { laiskey } = result
    let parcel = candidateAddresses.find(
      parcel => parcel.LAIS_KEY === laiskey,
    )


    dispatch(updatePlanningSearchValue(parcel?.SearchTerm))
    history.push(`${urlSubPath}/${replaceSpaceWithX(laiskey)}`)

    planningDetailsSearchRef?.current?.getElementsByTagName('input')?.item(0)?.blur()
  }

  /**
   * semantic-ui-react Search callback
   *
   * Called for every item in the array of results to be rendered.
   *
   * @param props
   * @returns {*}
   */
  const resultRenderer = ({ title }: SearchResultProps) => {
    return (
      <div className={'parcelSearchResultItem'}>{title}</div>
    )
  }

  /**
   * semantic-ui-react Search callback
   *
   * Called when the input has changed
   *
   * @param e
   * @param value
   */
  const handleSearchChange = (e: React.MouseEvent<HTMLElement>, { value }: SearchProps) => {
    dispatch(updatePlanningSearchValue(value))
    cancelAddressSearchRequest()
    if (value) performAddressSearch(value, dispatch)
  }

  return (
    <CSSTransition
      appear={true}
      in={true}
      exit={false}
      timeout={2000}
      classNames="parcel-search-bar">

      <div className={'planningDetailsSearchContainer'} ref={planningDetailsSearchRef}>
        <Search className={'planningDetailsSearch'}
                placeholder="Enter a street address..."
                loading={isLoading}
                onResultSelect={handleResultSelect}
                onSearchChange={handleSearchChange}
                results={getResults(candidateAddresses)}
                resultRenderer={resultRenderer}
                size={'big'}
                value={planningSearchValue}
                noResultsMessage="No property found"
                noResultsDescription="Enter the number of the property followed by the Street Name"
                minCharacters={1}
                input={<Input input={{ spellCheck: 'false', type: 'search' }}/>}
        />

      </div>

    </CSSTransition>
  )

}

export default PlanningSearchBar
